import { useEffect } from 'react';
import { toast } from 'sonner';
import { Navigate, Link, useNavigate, useParams } from 'react-router-dom';
import { Home, ShieldCheck, FileText, Cookie, Scale } from 'lucide-react';
import { LEGAL_TEXT, TERMS_TEXT, COOKIE_TEXT, NOTICE_TEXT } from '../content/legal';
import { usePlatform } from './BrandProvider';

const EMAIL = /([\w.+-]+@[\w-]+\.[\w.]+[a-z])/gi;

function Bold({ text }) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((s, i) => (s.startsWith('**') && s.endsWith('**') && s.length > 4 ? <b key={i} className="text-slate-100">{s.slice(2, -2)}</b> : s));
}

export function RichLine({ text, testid }) {
  return <span data-testid={testid} className="max-w-md text-center t-chat"><Rich text={text} /></span>;
}

function Rich({ text }) {
  return text.split(EMAIL).map((p, i) => (i % 2 ? <a key={i} href={`mailto:${p}`} className="text-cyan-300 underline underline-offset-2 hover:text-cyan-200 break-all">{p}</a> : <Bold key={i} text={p} />));
}

export function MarkdownText({ src }) {
  return parse(src).map((b, i) => {
    if (b.t === 'h1') return <h3 key={i} className="text-lg font-bold text-white mt-4 mb-2">{b.v}</h3>;
    if (b.t === 'h2') return <h4 key={i} className="text-sm font-bold text-cyan-200 mt-4 mb-1">{b.v}</h4>;
    if (b.t === 'ul') return <ul key={i} className="list-disc pl-5 space-y-1 my-2 marker:text-violet-400">{b.items.map((v, j) => <Item key={j} v={v} />)}</ul>;
    if (b.t === 'gap') return <div key={i} className="h-2" />;
    return <p key={i} className="my-1"><Rich text={b.v} /></p>;
  });
}

function parse(src) {
  const blocks = [];
  let list = null;
  for (const raw of src.trim().split('\n')) {
    const line = raw.trim();
    if (!line) { list = null; blocks.push({ t: 'gap' }); continue; }
    if (line.startsWith('- ')) { if (!list) { list = { t: 'ul', items: [] }; blocks.push(list); } list.items.push(line.slice(2)); continue; }
    list = null;
    if (line.startsWith('## ')) blocks.push({ t: 'h2', v: line.slice(3) });
    else if (line.startsWith('# ')) blocks.push({ t: 'h1', v: line.slice(2) });
    else blocks.push({ t: 'p', v: line });
  }
  return blocks;
}

function Item({ v }) {
  const i = v.indexOf(': ');
  if (i > 0 && i < 70) return <li><b className="text-slate-100">{v.slice(0, i + 1)}</b> <Rich text={v.slice(i + 2)} /></li>;
  return <li><Rich text={v} /></li>;
}

export const LEGAL_TABS = [
  { id: 'privacy', label: 'Privacy', icon: ShieldCheck, key: 'privacyPolicy', def: LEGAL_TEXT },
  { id: 'termini', label: 'Termini', icon: FileText, key: 'termsOfService', def: TERMS_TEXT },
  { id: 'cookie', label: 'Cookie', icon: Cookie, key: 'cookiePolicy', def: COOKIE_TEXT },
  { id: 'note', label: 'Note Legali', icon: Scale, key: 'legalNotice', def: NOTICE_TEXT },
];

export default function LegalPage() {
  const { legal, features } = usePlatform();
  const { tab } = useParams();
  if (!features.enableLegalFooter) return <ModuleOff />;
  const cur = LEGAL_TABS.find((t) => t.id === tab);
  if (!cur) return <Navigate to="/legal/privacy" replace />;
  return <LegalInner cur={cur} legal={legal} />;
}

export function ModuleOff() {
  useEffect(() => { toast('Questa sezione non è disponibile al momento'); }, []);
  return <Navigate to="/" replace />;
}

function Tabs({ cur }) {
  return (
    <nav data-testid="legal-tabs" className="max-w-3xl mx-auto px-4 sm:px-8 pb-3 flex flex-wrap gap-2">
      {LEGAL_TABS.map(({ id, label, icon: Icon }) => (
        <Link key={id} to={`/legal/${id}`} replace data-testid={`legal-tab-${id}`} aria-current={cur.id === id ? 'page' : undefined}
          className={`flex items-center gap-1.5 rounded-full px-3 sm:px-4 py-2 text-xs font-semibold border transition-colors ${cur.id === id ? 'bg-cyan-500/15 border-cyan-400/60 text-cyan-100' : 'border-white/10 text-slate-400 hover:bg-white/5'}`}>
          <Icon className="h-3.5 w-3.5" />{label}
        </Link>
      ))}
    </nav>
  );
}

function LegalInner({ cur, legal }) {
  const nav = useNavigate();
  const home = () => nav('/', { replace: true });
  const blocks = parse(legal[cur.key] || cur.def);
  useEffect(() => { window.scrollTo(0, 0); }, [cur.id]);
  return (
    <div data-testid="legal-view" className="titan-root min-h-[100dvh]">
      <header className="sticky top-0 z-30 pt-safe border-b border-violet-500/15 bg-titan-bg/85 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto h-14 px-4 sm:px-8 flex items-center gap-3">
          <span className="text-sm font-bold text-slate-100 flex items-center gap-2 truncate"><Scale className="h-4 w-4 text-cyan-300 shrink-0" />Centro legale</span>
          <button data-testid="legal-back-btn" onClick={home} className="ml-auto flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold bg-gradient-to-r from-violet-600 to-cyan-600 hover:brightness-110 transition-[filter]"><Home className="h-4 w-4" />Torna allo Store</button>
        </div>
        <Tabs cur={cur} />
      </header>
      <main className="relative z-10 max-w-3xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <article key={cur.id} data-testid="legal-content" className="glass glass-edge rounded-3xl px-5 py-7 sm:px-10 sm:py-12 t-chat text-[15px] leading-7 text-slate-300 rise break-words">
          {blocks.map((b, i) => {
            if (b.t === 'h1') return <h1 key={i} className="text-3xl sm:text-4xl font-extrabold tracking-tight text-white mb-6 leading-tight">{b.v}</h1>;
            if (b.t === 'h2') return <h2 key={i} className="text-base md:text-lg font-bold text-cyan-200 mt-10 mb-3 pt-6 border-t border-white/5">{b.v}</h2>;
            if (b.t === 'ul') return <ul key={i} className="list-disc pl-5 space-y-2.5 my-3 marker:text-violet-400">{b.items.map((v, j) => <Item key={j} v={v} />)}</ul>;
            if (b.t === 'gap') return <div key={i} className="h-2" />;
            return <p key={i} className="my-1"><Rich text={b.v} /></p>;
          })}
          {legal.lastUpdatedDate && <p data-testid="legal-updated" className="mt-10 pt-6 border-t border-white/5 text-xs text-slate-500">Ultimo aggiornamento: {legal.lastUpdatedDate}</p>}
        </article>
        <div className="flex justify-center mt-8 pb-10">
          <button data-testid="legal-bottom-back-btn" onClick={home} className="flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold border border-cyan-500/40 text-cyan-100 hover:bg-cyan-500/10 transition-colors"><Home className="h-4 w-4" />Torna allo Store</button>
        </div>
      </main>
    </div>
  );
}
